import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/components/base/Toast';
import EkycFlow from '@/components/feature/EkycFlow';
import { myLatestEkyc } from '@/api/ekyc';

export default function EkycPage() {
  const { user, reloadUser } = useAuth();
  // Lets a rejected or incomplete submission re-enter the capture flow without
  // writing a fabricated status the server does not hold.
  const [retrying, setRetrying] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/dang-nhap');
      return;
    }
    if (user.kycStatus === 'ekyc_verified') {
      if (user.role === 'Nông hộ') {
        navigate('/nong-ho/tong-quan');
      } else {
        navigate('/htx/tong-quan');
      }
    }
  }, [user, navigate]);

  if (!user) return null;

  // Both roles go through the same flow below: a Farmer submits against its
  // own Farmer profile (submitEkyc), an HTX against its own cooperative record
  // (submitHtxEkyc, reviewed by ADMIN/OPS) — EkycFlow already branches on
  // user.role for both. The HTX dashboard's own "Xác thực eKYC" banner had no
  // way to actually act on it while this page turned an HTX account away.
  const kycStatus = user.kycStatus || 'registered';

  if (kycStatus === 'ekyc_verified') return null;

  const handleBack = () => {
    if (user?.role === 'Nông hộ') {
      navigate('/nong-ho/tong-quan');
    } else {
      navigate('/htx/tong-quan');
    }
  };

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col max-w-[430px] mx-auto">
      <div className="flex items-center px-4 h-14 border-b border-gray-100 shrink-0">
        <button
          type="button"
          onClick={handleBack}
          className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-50 transition-colors"
          aria-label="Quay lại"
        >
          <i className="ri-arrow-left-line text-gray-700 text-lg" />
        </button>
        <h2 className="flex-1 text-center font-semibold text-gray-900 text-base">
          Xác thực danh tính
        </h2>
        <span className="w-9 h-9" aria-hidden="true" />
      </div>

      <div className="flex-1 overflow-y-auto">
        {retrying || kycStatus === 'registered' ? (
          <EkycFlow />
        ) : (
          <>
            {kycStatus === 'ekyc_pending' && <EkycPending user={user} reloadUser={reloadUser} onEdit={() => setRetrying(true)} onContinue={handleBack} />}
            {kycStatus === 'ekyc_need_more' && <EkycNeedMore user={user} onRetry={() => setRetrying(true)} />}
            {kycStatus === 'ekyc_failed' && <EkycFailed user={user} onRetry={() => setRetrying(true)} />}
          </>
        )}
      </div>
    </div>
  );
}

function EkycPending({ user, reloadUser, onEdit, onContinue }: {
  user: { displayName: string; role: string; kycReason?: string };
  reloadUser: () => Promise<unknown>;
  onEdit: () => void;
  onContinue: () => void;
}) {
  const [checking, setChecking] = useState(false);
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);

  // The submission date used to be `new Date()`, i.e. always today — it described
  // the moment the screen rendered, not when the file was sent, so a farmer
  // waiting three days still read today's date.
  useEffect(() => {
    myLatestEkyc()
      .then((record) => setSubmittedAt(record?.createdAt ?? null))
      // Only this one line degrades; the rest of the screen stands on its own.
      .catch(() => setSubmittedAt(null));
  }, []);

  // Re-reads the session from the server. A window.location.reload() also picked
  // up an approval, but on the far more common "still pending" path it dropped the
  // user back on an identical screen with no sign anything had happened — which
  // reads as a dead button.
  const handleCheck = async () => {
    setChecking(true);
    try {
      await reloadUser();
      // On approval EkycPage's effect navigates away, so reaching this line
      // means the status has not moved yet.
      showToast('Hồ sơ vẫn đang được xác thực. Vui lòng thử lại sau ít phút.', 'info');
    } catch {
      showToast('Không kiểm tra được trạng thái. Vui lòng thử lại.', 'error');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center px-4 py-10">
      <div className="w-24 h-24 rounded-full bg-amber-50 flex items-center justify-center mb-6">
        <span className="w-12 h-12 flex items-center justify-center">
          <i className="ri-time-line text-amber-500 text-4xl" />
        </span>
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">Đang chờ xác thực</h3>
      <p className="text-sm text-gray-500 text-center mb-1">
        Hồ sơ eKYC của bạn đang được hệ thống xác thực.
      </p>
      <p className="text-xs text-gray-400 text-center mb-4">
        Bấm "Kiểm tra lại" để cập nhật trạng thái.
      </p>
      <div className="w-full mb-6 rounded-xl border border-agrida-100 bg-agrida-50 px-4 py-3 text-xs text-agrida-800">
        Trong lúc chờ, bạn vẫn dùng được mọi chức năng. Chỉ xin hạn mức và vay vốn mới cần eKYC đã được duyệt.
      </div>

      {user.kycReason && (
        <div className="w-full bg-amber-50 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-information-line text-amber-600" />
            </span>
            <div>
              <p className="text-sm font-medium text-amber-800">Ghi chú</p>
              <p className="text-xs text-amber-600 mt-1">{user.kycReason}</p>
            </div>
          </div>
        </div>
      )}

      <div className="w-full bg-amber-50 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-information-line text-amber-600" />
          </span>
          <div>
            <p className="text-sm font-medium text-amber-800">Thông tin hồ sơ</p>
            <p className="text-xs text-amber-600 mt-1">
              Tên: <strong>{user.displayName}</strong>
            </p>
            <p className="text-xs text-amber-600">
              Loại tài khoản: <strong>{user.role}</strong>
            </p>
            <p className="text-xs text-amber-600">
              Ngày gửi: <strong>{submittedAt ? new Date(submittedAt).toLocaleDateString('vi-VN') : '—'}</strong>
            </p>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onContinue}
        className="w-full mb-3 h-12 rounded-xl bg-agrida-600 text-white text-sm font-semibold"
      >
        Tiếp tục sử dụng ứng dụng
      </button>
      <button
        type="button"
        onClick={onEdit}
        className="w-full mb-4 h-12 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold"
      >
        Chỉnh sửa hồ sơ
      </button>

      <p className="text-xs text-gray-400 text-center">
        Bạn sẽ nhận được thông báo khi hồ sơ được xử lý.{' '}
        <button
          type="button"
          className="text-agrida-600 font-medium disabled:opacity-50"
          onClick={handleCheck}
          disabled={checking}
        >
          {checking ? 'Đang kiểm tra…' : 'Kiểm tra lại'}
        </button>
      </p>
    </div>
  );
}

function EkycNeedMore({ user, onRetry }: {
  user: { id: string; displayName: string; kycReason?: string };
  onRetry: () => void;
}) {
  const handleResubmit = () => {
    onRetry();
    showToast('Đã chuyển sang chế độ chụp lại. Vui lòng thực hiện lại các bước.', 'info');
  };

  return (
    <div className="flex flex-col items-center px-4 py-6">
      <div className="w-24 h-24 rounded-full bg-orange-50 flex items-center justify-center mb-6">
        <span className="w-12 h-12 flex items-center justify-center">
          <i className="ri-error-warning-line text-orange-500 text-4xl" />
        </span>
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">Cần bổ sung thông tin</h3>
      <p className="text-sm text-gray-500 text-center mb-6">
        Hồ sơ eKYC của bạn cần được bổ sung thêm một số thông tin trước khi được phê duyệt.
      </p>

      <div className="w-full bg-orange-50 rounded-xl p-4 mb-5">
        <div className="flex items-start gap-3">
          <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-alert-line text-orange-600" />
          </span>
          <div>
            <p className="text-sm font-medium text-orange-800 mb-1">Lý do cần bổ sung</p>
            <p className="text-sm text-orange-700">{user.kycReason || 'Cần bổ sung thông tin định danh.'}</p>
          </div>
        </div>
      </div>

      <div className="w-full bg-gray-50 rounded-xl p-4 mb-6">
        <p className="text-xs font-medium text-gray-600 mb-3">Hướng dẫn bổ sung:</p>
        <ul className="space-y-2">
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-line text-agrida-500 text-xs" />
            </span>
            <span className="text-xs text-gray-600">Chụp lại ảnh CMND/CCCD rõ nét, đủ 2 mặt</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-line text-agrida-500 text-xs" />
            </span>
            <span className="text-xs text-gray-600">Đảm bảo ảnh không bị mờ, chói sáng hoặc khuất góc</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-line text-agrida-500 text-xs" />
            </span>
            <span className="text-xs text-gray-600">Chụp ảnh chân dung rõ mặt, không đeo kính đen</span>
          </li>
        </ul>
      </div>

      <button
        type="button"
        onClick={handleResubmit}
        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3.5 rounded-xl transition-colors whitespace-nowrap min-h-[48px] text-sm"
      >
        Chụp lại và gửi bổ sung
      </button>
    </div>
  );
}

function EkycFailed({ user, onRetry }: {
  user: { id: string; displayName: string; kycReason?: string };
  onRetry: () => void;
}) {
  const handleRetry = () => {
    onRetry();
    showToast('Vui lòng thực hiện lại eKYC từ đầu.', 'info');
  };

  return (
    <div className="flex flex-col items-center px-4 py-6">
      <div className="w-24 h-24 rounded-full bg-red-50 flex items-center justify-center mb-6">
        <span className="w-12 h-12 flex items-center justify-center">
          <i className="ri-close-circle-line text-red-500 text-4xl" />
        </span>
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">Xác thực thất bại</h3>
      <p className="text-sm text-gray-500 text-center mb-6">
        Hồ sơ eKYC của bạn không được phê duyệt. Vui lòng kiểm tra lại thông tin và thực hiện lại.
      </p>

      <div className="w-full bg-red-50 rounded-xl p-4 mb-5">
        <div className="flex items-start gap-3">
          <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-error-warning-line text-red-600" />
          </span>
          <div>
            <p className="text-sm font-medium text-red-800 mb-1">Lý do từ chối</p>
            <p className="text-sm text-red-700">{user.kycReason || 'Thông tin không hợp lệ. Vui lòng thực hiện lại.'}</p>
          </div>
        </div>
      </div>

      <div className="w-full bg-gray-50 rounded-xl p-4 mb-6">
        <p className="text-xs font-medium text-gray-600 mb-2">Lưu ý khi thực hiện lại:</p>
        <ul className="space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-arrow-right-line text-gray-400 text-xs" />
            </span>
            <span className="text-xs text-gray-600">Kiểm tra kỹ thông tin cá nhân trước khi gửi</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-arrow-right-line text-gray-400 text-xs" />
            </span>
            <span className="text-xs text-gray-600">Đảm bảo tên đăng ký khớp chính xác với CMND/CCCD</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-arrow-right-line text-gray-400 text-xs" />
            </span>
            <span className="text-xs text-gray-600">Ảnh chụp phải rõ nét, không chỉnh sửa</span>
          </li>
        </ul>
      </div>

      <button
        type="button"
        onClick={handleRetry}
        className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3.5 rounded-xl transition-colors whitespace-nowrap min-h-[48px] text-sm"
      >
        Thực hiện lại eKYC
      </button>
    </div>
  );
}
